import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Row, Col } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import DoneModal from '../modals/DoneModal';
import Checkbox from '../../../core/components/forms/Checkbox';
import Select from '../../../core/components/forms/Select';
import TicketItem from '../TicketItem';
import SellTicketsHeader from '../SellTicketsHeader';
import SellTicketsLayout from '../SellTicketsLayout';
import SaleOptionsRadio from '../forms/SaleOptionsRadio';
import extractEventId from '../../utils/helpers';
import withAsyncLifecycle from '../../../core/hoc/async-lifecycle';
import formatFloatWithSuperscript from '../../../core/utils/helpers';
import { selectSellFormData, selectSellSelectedEvent, selectSellTickets } from '../../selectors/sell-tickets';
import { selectHttpInfo } from '../../../core/selectors/http';
import { setFieldVisibility, setFieldValue } from '../../actions/forms';
import { getTickets, publishTickets, SELL_GET_TICKETS_REQUEST_NAME } from '../../actions/sell-tickets';
import { SELL_STEP_5 } from '../../constants/steps';
import { SALE_OPTIONS_ALLOW_TYPE_SALE_FIELD, SALE_OPTIONS_FORM, SALE_OPTIONS_SEAT_TOGETHER_FIELD,
    SALE_OPTIONS_TERMS_FIELD } from '../../reducers/features';
import { localeSelector } from '../../../core/selectors/i18n';

const SellStep5 = () => {
    const { isFetching, isError } = useSelector((state) => selectHttpInfo(state, SELL_GET_TICKETS_REQUEST_NAME));
    const tickets = useSelector(selectSellTickets);
    const dispatch = useDispatch();
    useEffect(() => {
        const eventId = extractEventId();
        dispatch(getTickets(SELL_STEP_5, eventId));
    }, []);
    const SellStep5WithAsyncLifecycle = withAsyncLifecycle(SellStep5Content, { isFetching, isError });
    return <SellStep5WithAsyncLifecycle tickets={tickets}/>;
};

const SellStep5Content = ({ tickets }) => {
    const locale = useSelector(localeSelector);
    const navigate = useNavigate();
    const event = useSelector(selectSellSelectedEvent);
    const dispatch = useDispatch();
    const inputs = useSelector((state) => selectSellFormData(state, SALE_OPTIONS_FORM));
    useEffect(() => {
        if (event.noOfTickets <= 1) {
            dispatch(setFieldVisibility(SALE_OPTIONS_FORM, SALE_OPTIONS_ALLOW_TYPE_SALE_FIELD, false));
        } else {
            dispatch(setFieldVisibility(SALE_OPTIONS_FORM, SALE_OPTIONS_ALLOW_TYPE_SALE_FIELD, true));
        }
        if (event.withSeats === false || event.noOfTickets <= 1) {
            dispatch(setFieldVisibility(SALE_OPTIONS_FORM, SALE_OPTIONS_SEAT_TOGETHER_FIELD, false));
        }
        if (event.withSeats === true && event.noOfTickets > 1) {
            dispatch(setFieldVisibility(SALE_OPTIONS_FORM, SALE_OPTIONS_SEAT_TOGETHER_FIELD, true));
        }
    }, []);

    return (
        <SellTicketsLayout>
            <DoneModal/>
            <Row>
                <Col lg={{ span: 10, offset: 1 }} md={12}>
                    <SellTicketsHeader title={<FormattedMessage id="sell_one_final_step"/>}
                        subtitle={<FormattedMessage id="sell_check_information_below"/>}/>
                    <Row>
                        <Col lg={8} md={7} sm={12} className="tickets-list">
                            <div className="tickets-container ticket-confirm-container mb-3">
                                {tickets.map((ticket) => (
                                    <div className="ticket-container border rounded mb-3" key={ticket.code}>
                                        <Row>
                                            <Col xl={7} lg={6} md={12}>
                                                <TicketItem ticket={ticket} event={event}/>
                                            </Col>
                                            <Col xl={5} lg={6} md={12} className="ticket-container-actions">
                                                <div className="event_actions">
                                                    <div className="event_price_container">
                                                        <div className="event_price_description">
                                                            <div className="fw-bolder mb-2">
                                                                Sumar
                                                            </div>
                                                            <div className="event_price_line">
                                                                <span className="text-muted">
                                                                    <FormattedMessage id="sell_new_price"/>
                                                                </span>
                                                                <span>
                                                                    {formatFloatWithSuperscript(ticket.sellPrice)} {ticket.currency}
                                                                </span>
                                                            </div>
                                                            <div className="event_price_line">
                                                                <span className="text-muted">
                                                                    <FormattedMessage id="sell_tax"/>
                                                                </span>
                                                                <span>
                                                                    -{formatFloatWithSuperscript(ticket.issuanceFee)} {ticket.currency}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="event_price_line mt-3 pt-3 border-top">
                                                            <span className="text-muted">
                                                                <FormattedMessage id="sell_your_money"/>
                                                            </span>
                                                            <span
                                                                className="event_price event_new_price fw-bold">{formatFloatWithSuperscript(ticket.sellPrice - ticket.issuanceFee)} {ticket.currency}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </div>
                        </Col>
                        <Col lg={4} md={5} sm={12} className="tickets-actions-terms">
                            <div className="bg-primary-subtle rounded p-3">
                                {inputs[SALE_OPTIONS_ALLOW_TYPE_SALE_FIELD].isVisible
                                    && <SaleOptionsRadio inputs={inputs} dispatch={dispatch}
                                        setFieldValue={setFieldValue}/>}
                                {inputs[SALE_OPTIONS_SEAT_TOGETHER_FIELD].isVisible
                                    && <Select
                                        controlId={SALE_OPTIONS_SEAT_TOGETHER_FIELD}
                                        label={<FormattedMessage id="sell_seat_together"/>}
                                        isInvalid={!inputs[SALE_OPTIONS_SEAT_TOGETHER_FIELD].isValid}
                                        selectedValue={inputs[SALE_OPTIONS_SEAT_TOGETHER_FIELD].value}
                                        onChange={(e) => {
                                            dispatch(setFieldValue(SALE_OPTIONS_FORM, SALE_OPTIONS_SEAT_TOGETHER_FIELD, e.target.value));
                                        }}
                                        errorMessage={inputs[SALE_OPTIONS_SEAT_TOGETHER_FIELD].errorMessage}
                                        options={[
                                            { id: 'yes', name: useIntl().formatMessage({ id: 'yes' }) },
                                            { id: 'no', name: useIntl().formatMessage({ id: 'no' }) },
                                        ]}
                                    />
                                }
                                <Checkbox
                                    controlId={SALE_OPTIONS_TERMS_FIELD}
                                    checked={inputs[SALE_OPTIONS_TERMS_FIELD].value}
                                    label={<><FormattedMessage id="sell_agree_with"/> <Link
                                        to={`/${locale}/show/termeni-si-conditii`} className="text-decoration-none"
                                        target="_blank"><FormattedMessage id="sell_terms_and_conditions"/></Link></>}
                                    errorMessage={inputs[SALE_OPTIONS_TERMS_FIELD].errorMessage}
                                    onChange={(e) => {
                                        dispatch(setFieldValue(SALE_OPTIONS_FORM, SALE_OPTIONS_TERMS_FIELD, e.target.checked));
                                    }}
                                />
                            </div>
                            <div className="step-actions text-center mt-3 mb-5">
                                <Button className="w-100 mb-3" variant="primary" size="lg" onClick={() => dispatch(publishTickets(inputs))}>
                                    <FormattedMessage id="sell_publish_tickets"/></Button>
                                <Button variant="link" className="text-decoration-none me-2"
                                    onClick={() => navigate('/sell/4')}><FormattedMessage id="prev"/> </Button>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </SellTicketsLayout>
    );
};
export default SellStep5;
